import { useEffect, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Loader2, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * PayoutBreakdown — expands a payout, or a pending "settling to bank" date,
 * into the specific orders that make it up.
 *
 * Pass exactly one of:
 *   • `payoutId`      → `GET /api/stripe/payouts/{id}/orders`
 *   • `availableOn`   → `GET /api/stripe/pending/by-date/{YYYY-MM-DD}/orders`
 *
 * Both endpoints return the same shape (`charges`, `refunds`, `totals`), so the
 * rendering below is identical. Lazily fetched on mount; skeleton while loading;
 * empty-state when there's no order linkage yet (Stripe's data usually catches
 * up within a few hours of the batch).
 */
export default function PayoutBreakdown({ payoutId, availableOn }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const url = payoutId
    ? `/stripe/payouts/${payoutId}/orders`
    : availableOn
    ? `/stripe/pending/by-date/${availableOn}/orders`
    : null;
  const testKey = payoutId || `pending-${availableOn}`;

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(url);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Unable to load breakdown");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (loading) {
    return (
      <div className="px-4 py-6 text-sm text-muted-foreground flex items-center gap-2 justify-center bg-secondary/20">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading breakdown…
      </div>
    );
  }
  if (err) {
    return <div className="px-4 py-4 text-sm text-destructive bg-destructive/10">{err}</div>;
  }

  const charges = data?.charges || [];
  const refunds = data?.refunds || [];
  const totals = data?.totals || {};
  const label = payoutId ? "Orders in this payout" : "Orders settling this day";
  const emptyMsg = payoutId
    ? "No linked orders yet — Stripe usually populates this within a few hours of the payout."
    : "No charges settle on this date.";

  return (
    <div className="bg-secondary/20 border-t border-border/60" data-testid={`payout-breakdown-${testKey}`}>
      <div className="px-4 py-2 flex items-center justify-between border-b border-border/50 text-[11px] label-eyebrow">
        <span>{label}</span>
        <span className="text-muted-foreground normal-case tracking-normal font-normal">
          {totals.charge_count || 0} charges · fees {fmtCurrency((totals.fees || 0) / 100)}
          {refunds.length > 0 && ` · ${refunds.length} refunds`}
        </span>
      </div>

      {charges.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-muted-foreground">{emptyMsg}</div>
      ) : (
        <div className="divide-y divide-border/40">
          {charges.map((c, i) => {
            const o = c.order;
            return (
              <div key={i} className="px-4 py-2.5 flex items-center gap-3 hover:bg-white/60 transition-colors">
                <div className="font-mono text-xs w-24 shrink-0 truncate">
                  {o?.number || (
                    <span className="text-muted-foreground italic">not synced</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate flex items-center gap-2">
                    <span>{o?.customer_name || "—"}</span>
                    {o?.store_name && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded border border-border/60 bg-white text-muted-foreground">
                        {o.store_name}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                    Sales: <span className="text-foreground">{o?.sales_rep || "—"}</span>
                    {o?.po_number && (
                      <>
                        {" · "}PO <span className="font-mono">{o.po_number}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="num font-semibold text-sm">{fmtCurrency(c.net / 100)}</div>
                  <div className="text-[10px] text-muted-foreground">
                    gross {fmtCurrency(c.amount / 100)} · fee {fmtCurrency(c.fee / 100)}
                  </div>
                </div>
                {o?.id && (
                  <Link
                    to={`/orders/${o.id}`}
                    className="text-muted-foreground hover:text-primary shrink-0"
                    title="Open order"
                  >
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}

      {refunds.length > 0 && (
        <div className="border-t border-border/50 px-4 py-2 text-[11px] text-muted-foreground">
          Includes {refunds.length} refund(s) totaling{" "}
          <span className="font-medium text-foreground">
            {fmtCurrency(refunds.reduce((s, r) => s + Math.abs(r.net), 0) / 100)}
          </span>
        </div>
      )}
    </div>
  );
}
